import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import Button from '../../../../components/global/Button';
import { fileInputAccept } from '../../../../constants/photo-upload';
import videoExtensions from '../../../../apps/pro/views/Upload/VideoUpload/helpers';
import {
  isMainPhotosAlbum,
  isSpecialtyAlbum,
  isVideoAlbum,
} from '../../../../util/album';
import { ALBUM_SLIDESHOW_STATUS } from '../../../../constants/albums';
import {
  canUploadToSpecialtyEventAlbums,
  canViewSpecialtyEventAlbums,
} from '../helpers';

const videoExtensionsType = videoExtensions
  .map(extension => `.${extension}`)
  .join(',');

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  onUpload: PropTypes.func.isRequired,
  onUploadButtonClick: PropTypes.func.isRequired,
  onViewButtonClick: PropTypes.func.isRequired,
  title: PropTypes.string,
  authenticated: PropTypes.bool.isRequired,
  viewLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  uploadLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  isPhotosAlbums: PropTypes.bool,
};

const SpecialtyEventUploadAlbumBox = ({
  album,
  albumFolder,
  onUpload,
  onUploadButtonClick,
  onViewButtonClick,
  title,
  authenticated,
  viewLabel,
  uploadLabel,
  className,
  disabled,
  isPhotosAlbums,
}) => {
  const ref = useRef();
  const history = useHistory();
  const classes = classNames({
    'specialty-event-upload-album-box': true,
    [className]: !!className,
  });

  const isWaldofiedAlbum = isMainPhotosAlbum(album);
  const isVideoAlbumBool = isVideoAlbum(album);
  const photosAlbums = (albumFolder?.children?.albums?.records ?? []).filter(
    child => !isSpecialtyAlbum(child),
  );
  const { event_settings: eventSettings } = albumFolder ?? {};

  const canUpload = canUploadToSpecialtyEventAlbums(
    isPhotosAlbums ? photosAlbums : [album],
    authenticated,
    eventSettings,
  );

  const canView = canViewSpecialtyEventAlbums(
    isPhotosAlbums ? photosAlbums : [album],
    eventSettings,
  );

  const acceptedFiles = (() => {
    if (isVideoAlbumBool) {
      return videoExtensionsType;
    }

    if (isWaldofiedAlbum) {
      return [videoExtensionsType, fileInputAccept].join(',');
    }

    return fileInputAccept;
  })();

  return (
    <div className={classes}>
      {title && <p className="text-center text-bold h4">{title}</p>}
      {isWaldofiedAlbum &&
        new Date() >= new Date(eventSettings?.event_date) &&
        album.slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED && (
          <Button
            btnBlock
            btnSize="sm"
            className="live-slideshow-button"
            onClick={() =>
              history.push(`/albums/${album.id}/slideshow?contribute=1`)
            }
          >
            Add to Live Slideshow
          </Button>
        )}
      <div className="d-flex">
        {canUpload && (
          <Button
            btnBlock
            btnSize="sm"
            className="action-buttons"
            disabled={disabled}
            onClick={() => onUploadButtonClick(ref)}
          >
            {uploadLabel}
          </Button>
        )}
        {canView && (
          <Button
            btnBlock
            btnSize="sm"
            className="action-buttons"
            disabled={disabled}
            onClick={onViewButtonClick}
          >
            {viewLabel}
          </Button>
        )}
      </div>
      <input
        id="uploader"
        type="file"
        className="d-none"
        accept={acceptedFiles}
        multiple={!isVideoAlbumBool}
        disabled={disabled}
        onChange={event => {
          onUpload([...event.target.files], album);
        }}
        ref={ref}
      />
    </div>
  );
};

SpecialtyEventUploadAlbumBox.propTypes = propTypes;

export default SpecialtyEventUploadAlbumBox;
